import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { useState, type ReactNode } from 'react';

import { testIdAttr, testIdPart } from '@/lib/testIds';

import { Button } from './Button';

export interface FilterDefinition {
  /** Identifies the filter in the values object. */
  name: string;
  label: string;
  options: { value: string; label: string }[];
}

/** Two calendar days, as `YYYY-MM-DD`. Empty means that end is open. */
export interface DateRange {
  from: string;
  to: string;
}

/** Said against To when the range runs backwards. */
export const DATE_ORDER_MESSAGE = 'To must be on or after From.';

interface FilterBarProps {
  /** Leave out, with `onSearchChange`, for a list that has nothing to search. */
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  filters?: FilterDefinition[];
  /** Current value of each filter, keyed by its name. Empty means "any". */
  values?: Record<string, string>;
  onFilterChange?: (name: string, value: string) => void;
  /**
   * A From and To pair of date inputs, for a list about when things happened.
   * Changes are reported only when the range makes sense: To before From is
   * said against To and nothing is reported, so nothing is asked for.
   */
  dateRange?: DateRange;
  onDateRangeChange?: (range: DateRange) => void;
  /** Controls of a caller's own, drawn in the bar after the dropdowns. */
  extra?: ReactNode;
  /** True when something in `extra` is set, so Clear filters is offered. */
  extraActive?: boolean;
  onClear?: () => void;
  /**
   * Names the bar for the testing team's tests. The search box, the date
   * boxes, each dropdown (`<testId>-<filter name>-select`) and its options,
   * and Clear filters each get their own. See docs/test-ids.md.
   */
  testId: string;
}

/**
 * The longest search any list accepts: the backend refuses a longer one as a
 * malformed request, and Retry would only send the same search again.
 */
export const SEARCH_MAX_LENGTH = 200;

/**
 * Search plus dropdowns, the bar that sits above every list in the wireframes,
 * and a date range for the lists that are about when.
 */
export function FilterBar({
  searchValue = '',
  onSearchChange,
  searchPlaceholder = 'Search',
  filters = [],
  values = {},
  onFilterChange,
  dateRange,
  onDateRangeChange,
  extra,
  extraActive = false,
  onClear,
  testId,
}: FilterBarProps) {
  /*
   * What is typed into the two date boxes, which may run backwards for a moment
   * while somebody is changing both. The caller only hears about a range once
   * it is the right way round, so the list never asks the server for a range
   * the server would refuse.
   */
  const [draft, setDraft] = useState<DateRange | null>(null);
  const shown = draft ?? dateRange;
  const backwards = Boolean(shown?.from && shown.to && shown.to < shown.from);

  // A new range from outside - cleared, or preset - replaces whatever was
  // being typed. Adjusted while rendering so the stale draft is never shown.
  const [seen, setSeen] = useState(dateRange);
  if (dateRange !== seen) {
    setSeen(dateRange);
    setDraft(null);
  }

  function changeDate(part: keyof DateRange, value: string) {
    const next = { ...(shown ?? { from: '', to: '' }), [part]: value };
    if (next.from && next.to && next.to < next.from) {
      setDraft(next);
      return;
    }
    setDraft(null);
    onDateRangeChange?.(next);
  }

  const anythingSet =
    Boolean(searchValue) ||
    Object.values(values).some(Boolean) ||
    Boolean(dateRange?.from || dateRange?.to) ||
    extraActive;

  return (
    <Box
      data-testid={testId}
      sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}
    >
      {onSearchChange ? (
        <TextField
          type="search"
          value={searchValue}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={searchPlaceholder}
          size="small"
          slotProps={{
            htmlInput: {
              'aria-label': searchPlaceholder,
              maxLength: SEARCH_MAX_LENGTH,
              ...testIdAttr(`${testId}-search-input`),
            },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
          sx={{ width: { xs: '100%', sm: 260 } }}
        />
      ) : null}

      {shown ? (
        <>
          <TextField
            type="date"
            size="small"
            label="From"
            value={shown.from}
            onChange={(event) => changeDate('from', event.target.value)}
            // The label sits above the box: a date input always shows its own
            // placeholder, and a label on top of that would overlap it.
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: testIdAttr(`${testId}-from-input`),
            }}
            sx={{ width: 170 }}
          />
          <TextField
            type="date"
            size="small"
            label="To"
            value={shown.to}
            onChange={(event) => changeDate('to', event.target.value)}
            error={backwards}
            // TextField ties the helper text to the input, so the message is
            // read with To rather than floating unexplained below it.
            helperText={backwards ? DATE_ORDER_MESSAGE : undefined}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: testIdAttr(`${testId}-to-input`),
              formHelperText: backwards
                ? { role: 'alert', ...testIdAttr(`${testId}-to-error`) }
                : undefined,
            }}
            sx={{ width: 170 }}
          />
        </>
      ) : null}

      {filters.map((filter) => (
        <TextField
          key={filter.name}
          select
          size="small"
          label={filter.label}
          value={values[filter.name] ?? ''}
          onChange={(event) => onFilterChange?.(filter.name, event.target.value)}
          slotProps={{
            select: {
              SelectDisplayProps: testIdAttr(
                `${testId}-${testIdPart(filter.name)}-select`,
              ),
            },
          }}
          sx={{ minWidth: 170, width: 'auto' }}
        >
          <MenuItem
            value=""
            data-testid={`${testId}-${testIdPart(filter.name)}-option-none`}
          >
            Any
          </MenuItem>
          {filter.options.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              data-testid={`${testId}-${testIdPart(filter.name)}-option-${testIdPart(option.value)}`}
            >
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      ))}

      {extra}

      {anythingSet && onClear ? (
        <Button
          variant="ghost"
          size="sm"
          onClick={onClear}
          data-testid={`${testId}-clear-button`}
        >
          Clear filters
        </Button>
      ) : null}
    </Box>
  );
}
